import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

// Default icon paths (bundled locally, no CDN)
const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function ClickHandler({ onSelect }) {
  useMapEvents({
    click(e) {
      onSelect({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat && lng) map.setView([lat, lng], 14);
  }, [lat, lng, map]);
  return null;
}

export default function AddressMap({ address, lat, lng, onChange, readOnly = false, autoQuery = "" }) {
  const { t } = useI18n();
  const [q, setQ] = useState(address || "");
  const [autoStatus, setAutoStatus] = useState("");

  useEffect(() => {
    setQ(address || "");
  }, [address]);

  const geocode = async (query) => {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`;
    const r = await fetch(url, { headers: { Accept: "application/json" } });
    const data = await r.json();
    return data && data[0] ? data[0] : null;
  };

  // Auto-locate from structured address fields (debounced to respect Nominatim usage policy).
  useEffect(() => {
    if (!autoQuery || readOnly) {
      return undefined;
    }
    const timer = setTimeout(async () => {
      setQ(autoQuery);
      setAutoStatus("searching");
      try {
        const hit = await geocode(autoQuery);
        if (hit) {
          onChange({ address: autoQuery, lat: parseFloat(hit.lat), lng: parseFloat(hit.lon) });
          setAutoStatus("found");
        } else {
          onChange({ address: "", lat: null, lng: null });
          setAutoStatus("not_found");
        }
      } catch (_e) {
        onChange({ address: "", lat: null, lng: null });
        setAutoStatus("not_found");
      }
    }, 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoQuery, readOnly]);

  const search = async () => {
    if (!q.trim()) return;
    setAutoStatus("");
    try {
      const hit = await geocode(q);
      if (hit) {
        onChange({
          address: hit.display_name,
          lat: parseFloat(hit.lat),
          lng: parseFloat(hit.lon),
        });
      }
    } catch (_e) {
      /* ignore */
    }
  };

  const center = lat && lng ? [lat, lng] : [47.3769, 8.5417]; // default Zurich

  return (
    <div className="space-y-2">
      {!readOnly && (
        <div className="flex gap-2">
          <Input
            placeholder={t("address")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                search();
              }
            }}
            data-testid="address-search-input"
          />
          <Button type="button" variant="outline" onClick={search} data-testid="address-search-btn">
            <Search className="h-4 w-4" />
          </Button>
        </div>
      )}
      <div className="h-64 w-full overflow-hidden rounded-md border border-border">
        <MapContainer center={center} zoom={lat && lng ? 14 : 6} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {lat && lng && <Marker position={[lat, lng]} />}
          {!readOnly && (
            <ClickHandler
              onSelect={({ lat, lng }) => {
                setAutoStatus("");
                onChange({ address: q || address, lat, lng });
              }}
            />
          )}
          <Recenter lat={lat} lng={lng} />
        </MapContainer>
      </div>
      {!readOnly && (
        <div className="text-xs text-muted-foreground" data-testid="address-map-status">
          {autoStatus === "searching" && t("map_auto_searching")}
          {autoStatus === "found" && t("map_auto_found")}
          {autoStatus === "not_found" && t("map_auto_not_found")}
          {!autoStatus && t("click_map_to_set")}
        </div>
      )}
    </div>
  );
}
